import type { StudentSummary } from '@school-erp/types';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../api/http';
import { t, type MessageKey } from '../../i18n';

/** Parent portal home: only the signed-in parent's own children (the API enforces ownership). */
export function MyChildrenPage() {
  const children = useQuery({
    queryKey: ['me', 'children'],
    queryFn: () => api<StudentSummary[]>('/me/children'),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h1 className="text-2xl font-bold">{t('portal.title')}</h1>
      {children.isPending && <p role="status">{t('common.loading')}</p>}
      {children.isError && (
        <p role="alert" className="text-red-700">
          {t('common.loadError')}
        </p>
      )}
      {children.data?.length === 0 && <p className="text-slate-600">{t('portal.empty')}</p>}
      <ul className="space-y-3">
        {children.data?.map((child) => (
          <li key={child.id} className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="text-lg font-semibold">
              {child.firstName} {child.lastName}
            </h2>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <dt className="text-slate-600">{t('students.admissionNo')}</dt>
              <dd className="font-mono">{child.admissionNumber}</dd>
              <dt className="text-slate-600">{t('students.class')}</dt>
              <dd>
                {child.enrollment
                  ? `${child.enrollment.className}${child.enrollment.sectionName ? ` – ${child.enrollment.sectionName}` : ''}`
                  : t('students.notEnrolled')}
              </dd>
              <dt className="text-slate-600">{t('adm.status')}</dt>
              <dd>{t(`students.status.${child.status}` as MessageKey)}</dd>
              {child.enrollment?.rollNumber && (
                <>
                  <dt className="text-slate-600">{t('students.rollNumber')}</dt>
                  <dd>{child.enrollment.rollNumber}</dd>
                </>
              )}
            </dl>
            <Link
              to={`/my-children/${child.id}`}
              className="mt-3 inline-block text-sm text-brand-700 underline"
              aria-label={`${t('child.open')}: ${child.firstName} ${child.lastName}`}
            >
              {t('child.open')}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
